import { OperationStatus } from "~/core/models/OperationStatus.enum";
import { DtwinMetadataStatus } from "~/modules/dtwin/models/DtwinMetadataStatus.enum";

// Colors used for the simplified pictogram representations of the device metastatus.
const defaultMetastatusColor = "gray-600";
const successMetastatusColor = "success-700";
const mixedMetastatusColor = "warning-500";
const errorMetastatusColor = "error-700";

/**
 * Function used to compute the global metadata status to be used in the LxcDtwinsListOperationStatus component. It
 * computes the all the metastatus statuses and return a global status which describe if all the operations can be
 * considered as successful, in failure or mixed (mix of success and failures). If there is no metastatus statuses,
 * return an unknown global status.
 * @param operationStatuses The metastatus statuses to be computed to determine the global metadata status.
 */
export function compute(
  operationStatuses: Record<string, OperationStatus> | undefined,
): DtwinMetadataStatus {
  if (
    operationStatuses !== undefined &&
    Object.keys(operationStatuses).length !== 0
  ) {
    const values = Object.values(operationStatuses);
    const valuesSimplified = new Set(values);
    if (
      (valuesSimplified.has(OperationStatus.DONE) ||
        valuesSimplified.has(OperationStatus.CANCELLED)) &&
      !(
        valuesSimplified.has(OperationStatus.FAILED) ||
        valuesSimplified.has(OperationStatus.EXPIRED)
      )
    ) {
      return DtwinMetadataStatus.SUCCESS;
    } else if (
      (valuesSimplified.has(OperationStatus.FAILED) ||
        valuesSimplified.has(OperationStatus.EXPIRED)) &&
      !(
        valuesSimplified.has(OperationStatus.DONE) ||
        valuesSimplified.has(OperationStatus.CANCELLED)
      )
    ) {
      return DtwinMetadataStatus.ERROR;
    }
    return DtwinMetadataStatus.MIXED;
  }
  return DtwinMetadataStatus.UNKNOWN;
}

/**
 * Compute the color associated to the operation statuses.
 * @param operationStatuses
 */
export const computeOperationStatusesColor: (
  operationStatuses: Record<string, OperationStatus> | undefined,
) => string = (
  operationStatuses: Record<string, OperationStatus> | undefined,
) => {
  const metastatus = compute(operationStatuses);
  let color = defaultMetastatusColor;
  switch (metastatus) {
    case DtwinMetadataStatus.SUCCESS:
      color = successMetastatusColor;
      break;
    case DtwinMetadataStatus.MIXED:
      color = mixedMetastatusColor;
      break;
    case DtwinMetadataStatus.ERROR:
      color = errorMetastatusColor;
      break;
  }
  return color;
};
